import pLimit from "p-limit";
import { API, graphqlOperation } from "aws-amplify";

import {
  listCarrierCompanyIds,
  listCarriersForCompanyBackfill,
} from "../../graphql/carrier/carrier-queries";
import { touchCarrier } from "../../graphql/carrier/carrier-mutations";
import { logError } from "../../utils/appUtils";

/**
 * Place every carrier entry on a company, for the entries written before the
 * company records existed.
 *
 * The work itself is not done here. handleCarrierChange already places an entry
 * the moment it sees one with no companyId — resolving it against the claim table,
 * deriving the company id so two entries cannot make two companies, refusing a
 * merge where two operating authorities contradict each other, and creating the
 * company and its claims in the one place those tables can be written. So this
 * walks the carrier book and *touches* each unplaced row, and the resolver does
 * the placing.
 *
 * Which is not a shortcut but the only correct shape for it. A client-side
 * backfill would need to write CarrierIdentifierClaim, and that table generates
 * no mutations on purpose — it is the lambda's, so that the first claim on a
 * number wins under a condition rather than under whoever polled last. A second
 * copy of the grouping rules out here would also be a second copy free to drift
 * from the one every live write goes through, which is exactly the split this
 * whole layer exists to prevent.
 *
 * Safe to run twice. An entry already carrying a companyId is skipped, and a
 * touch is an empty update — the row's own values are not rewritten, so nothing
 * is lost if the run is interrupted half way and started again.
 *
 * Admin only, and that is not incidental: a yard's session can read its own
 * carrier rows and no others, so run as anyone else this would report the book as
 * already done.
 */

const PAGE = 500;

// Ten at a time. Each touch becomes a stream record and a handful of conditional
// writes behind it, and the resolver is the throughput that matters here — going
// wider only queues the same work up in the shard.
const TOUCH_CONCURRENCY = 10;

// How long to keep asking whether the stream has caught up, so the run can report
// what actually landed rather than what it asked for. The resolver is usually
// done within a second or two of the touch; the ceiling is here so a backed-up
// shard ends as an honest "still working" instead of hanging the dashboard.
const VERIFY_ATTEMPTS = 5;
const VERIFY_DELAY_MS = 3000;

const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Every page of a list query, as one array. */
const pageAll = async (document, pick) => {
  const items = [];
  let nextToken = null;

  do {
    const response = await API.graphql(
      graphqlOperation(document, { limit: PAGE, nextToken }),
    );
    const page = pick(response?.data);
    items.push(...(page?.items?.filter(Boolean) || []));
    nextToken = page?.nextToken || null;
  } while (nextToken);

  return items;
};

const fetchCarriers = () =>
  pageAll(listCarriersForCompanyBackfill, (data) => data?.listCarriers);

const countCompanies = async () =>
  (await pageAll(listCarrierCompanyIds, (data) => data?.listCarrierCompanys))
    .length;

/**
 * Oldest touched last, so the company keeps the spelling of the name the carrier
 * was first entered under.
 *
 * The business name is last-writer-wins between a company and its entries, and
 * every entry in a group will write it once as this run reaches it. Ordering the
 * run is what decides which spelling is written last — and an older entry is the
 * one whose loads, invoices and shipment history are already filed under it. So
 * the sort is newest first and the oldest entry brings up the rear, having the
 * last word on the name.
 *
 * A tendency rather than a guarantee: the touches go out several at a time and
 * the resolver answers them off the stream, so the order they land in is close to
 * this one and not promised to be it. The stake is a spelling, and the carrier
 * can correct it in its own portal.
 */
const oldestLast = (carriers) =>
  carriers
    .slice()
    .sort((a, b) =>
      String(b.createdAt || "").localeCompare(String(a.createdAt || "")),
    );

export const backfillCarrierCompanies = async (notifyCallback) => {
  console.clear();
  console.log("Starting carrier company backfill…");

  try {
    const carriers = await fetchCarriers();
    const unplaced = oldestLast(
      carriers.filter((carrier) => carrier.id && !carrier.companyId),
    );
    const companiesBefore = await countCompanies();

    console.log(
      `${carriers.length} carrier entr${carriers.length === 1 ? "y" : "ies"}, ${
        carriers.length - unplaced.length
      } already placed, ${unplaced.length} to place. ${companiesBefore} compan${
        companiesBefore === 1 ? "y" : "ies"
      } so far.`,
    );

    if (unplaced.length === 0) {
      notifyCallback?.(
        `Nothing to do: all ${carriers.length} carrier entries are already on a company (${companiesBefore} companies).`,
        "info",
      );
      return;
    }

    const limiter = pLimit(TOUCH_CONCURRENCY);
    const results = await Promise.all(
      unplaced.map((carrier) =>
        limiter(async () => {
          try {
            await API.graphql(
              graphqlOperation(touchCarrier, { input: { id: carrier.id } }),
            );
            return "touched";
          } catch (err) {
            // Named, because the reason one row failed is the whole diagnosis —
            // a carrier the resolver cannot place is usually one whose number
            // another company already claimed.
            console.warn(
              `backfillCarrierCompanies: carrier ${carrier.id} "${carrier.businessName}" at yard ${carrier.reloadYardId}:`,
              err,
            );
            return "failed";
          }
        }),
      ),
    );

    const touched = results.filter((result) => result === "touched").length;
    const failed = results.filter((result) => result === "failed").length;
    console.log(`Touched ${touched} entries, ${failed} failed. Verifying…`);

    // The resolver runs off the stream, so the touch above is a request rather
    // than a result. Re-read until the entries are stamped, and report whichever
    // way it ends.
    let remaining = unplaced.length;
    for (let attempt = 1; attempt <= VERIFY_ATTEMPTS; attempt += 1) {
      await pause(VERIFY_DELAY_MS);
      const after = await fetchCarriers();
      remaining = after.filter(
        (carrier) => carrier.id && !carrier.companyId,
      ).length;
      console.log(
        `Verify ${attempt}/${VERIFY_ATTEMPTS}: ${remaining} unplaced`,
      );
      if (remaining === 0) break;
    }

    const companiesAfter = await countCompanies();
    const summary = `Carrier company backfill: ${touched} entries touched, ${
      companiesAfter - companiesBefore
    } new companies (${companiesAfter} total)`;
    console.log(
      `${summary}. ${remaining} still unplaced, ${failed} touch(es) failed.`,
    );

    if (remaining === 0 && failed === 0) {
      notifyCallback?.(`${summary}. All entries placed.`, "info");
      return;
    }

    notifyCallback?.(
      `${summary}. ${remaining} still unplaced${
        failed > 0 ? `, ${failed} failed` : ""
      } — run it again, then check the console and the Log table.`,
      "warning",
    );
  } catch (err) {
    logError("AdminDashboard:backfillCarrierCompanies", err, notifyCallback);
    notifyCallback?.(
      "backfillCarrierCompanies failed. Check console.",
      "error",
    );
  }
};
